/**
 * Hooks du compte : état civil, profil, préférences.
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 *
 * Les corps envoyés sont **partiels** : le client ne transmet qu'un champ qu'il
 * a l'intention de changer, et le serveur laisse les autres tels quels
 * (docs/08 §3). Envoyer systématiquement tout le formulaire écraserait ce qu'un
 * autre onglet vient d'enregistrer.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { accountKeys, authKeys } from "@/shared/api/query-keys";
import { applyTheme, toTheme } from "@/shared/theme/theme";

export type Account = components["schemas"]["UserAccountResponse"];
export type AccountUpdate = components["schemas"]["UpdateAccountRequest"];
export type Profile = components["schemas"]["UserProfileResponse"];
export type ProfileUpdate = components["schemas"]["UpdateProfileRequest"];
export type Settings = components["schemas"]["UserSettingsResponse"];
export type SettingsUpdate = components["schemas"]["UpdateSettingsRequest"];
export type AccountDeletion = components["schemas"]["AccountDeletionResponse"];
export type PersonalDataExport = components["schemas"]["PersonalDataExportResponse"];

export function useAccount(): UseQueryResult<Account, ApiError> {
  return useQuery<Account, ApiError>({
    queryKey: accountKeys.me(),
    queryFn: () => unwrap(api.GET("/api/v1/users/me")),
  });
}

export function useUpdateAccount(): UseMutationResult<Account, ApiError, AccountUpdate> {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  return useMutation<Account, ApiError, AccountUpdate>({
    mutationFn: (body) => unwrap(api.PATCH("/api/v1/users/me", { body })),
    onSuccess: (account) => {
      queryClient.setQueryData(accountKeys.me(), account);
      // La vue de session porte le nom affiché de la barre de navigation :
      // sans cette invalidation, l'écran de compte et l'en-tête ne diraient
      // plus la même chose jusqu'au prochain rechargement.
      void queryClient.invalidateQueries({ queryKey: authKeys.me() });
      // « Ma langue » enregistrée sans changer la langue affichée serait un
      // réglage décoratif : l'utilisateur devrait encore basculer à la main.
      if (account.locale !== i18n.resolvedLanguage) {
        void i18n.changeLanguage(account.locale);
      }
    },
  });
}

export function useProfile(): UseQueryResult<Profile, ApiError> {
  return useQuery<Profile, ApiError>({
    queryKey: accountKeys.profile(),
    queryFn: () => unwrap(api.GET("/api/v1/users/me/profile")),
  });
}

export function useUpdateProfile(): UseMutationResult<Profile, ApiError, ProfileUpdate> {
  const queryClient = useQueryClient();
  return useMutation<Profile, ApiError, ProfileUpdate>({
    mutationFn: (body) => unwrap(api.PATCH("/api/v1/users/me/profile", { body })),
    onSuccess: (profile) => {
      // La réponse porte l'état **après** normalisation côté serveur (doublons
      // retirés, blancs rognés). L'écrire dans le cache évite d'afficher ce que
      // l'utilisateur a tapé plutôt que ce qui a été enregistré.
      queryClient.setQueryData(accountKeys.profile(), profile);
    },
  });
}

export function useSettings(): UseQueryResult<Settings, ApiError> {
  return useQuery<Settings, ApiError>({
    queryKey: accountKeys.settings(),
    queryFn: async () => {
      const settings = await unwrap(api.GET("/api/v1/users/me/settings"));
      applyTheme(toTheme(settings.theme));
      return settings;
    },
  });
}

export function useUpdateSettings(): UseMutationResult<Settings, ApiError, SettingsUpdate> {
  const queryClient = useQueryClient();
  return useMutation<Settings, ApiError, SettingsUpdate>({
    mutationFn: (body) => unwrap(api.PATCH("/api/v1/users/me/settings", { body })),
    onSuccess: (settings) => {
      queryClient.setQueryData(accountKeys.settings(), settings);
      applyTheme(toTheme(settings.theme));
    },
  });
}

/**
 * Export RGPD, téléchargé sous forme de fichier JSON.
 *
 * `useMutation` et non `useQuery` : l'appel écrit au journal d'audit côté
 * serveur, et TanStack Query rejouerait une requête au remontage du composant
 * — chaque retour sur l'écran laisserait une trace d'un export que personne
 * n'a demandé.
 */
export function useExportMyData(): UseMutationResult<PersonalDataExport, ApiError, void> {
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/users/me/export")),
    onSuccess: (data) => {
      downloadJson(data, `teach-donnees-${data.exported_at.slice(0, 10)}.json`);
    },
  });
}

export function useRequestAccountDeletion(): UseMutationResult<AccountDeletion, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE("/api/v1/users/me")),
    onSuccess: () => {
      // Invalidation plutôt qu'écriture directe dans le cache : la réponse ne
      // porte que l'état de la demande, alors que le bandeau se lit sur le
      // compte entier. Recopier deux champs d'un objet dans un autre finirait
      // par les laisser diverger.
      void queryClient.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export function useCancelAccountDeletion(): UseMutationResult<AccountDeletion, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/users/me/deletion/cancel")),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

/**
 * Déclenche le téléchargement d'un objet sous forme de fichier JSON.
 *
 * L'URL objet est révoquée aussitôt : sans cela, l'archive — qui contient
 * l'intégralité des données personnelles — resterait allouée dans l'onglet
 * jusqu'à sa fermeture.
 */
function downloadJson(payload: unknown, filename: string): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
